<i18n locale="en">
{
  "features-column": "Features",
  "included": "Included in {plan}",
  "not-included": "Not included in {plan}"
}
</i18n>
<template>
  <table
    class="plan-comparison-table w-full"
    data-testid="plan-comparison-table"
  >
    <caption v-if="caption" class="plan-comparison-table__caption text-left">
      <OwnType :text="caption" variant="title-small" color="primary" />
    </caption>
    <thead>
      <tr>
        <th class="text-left" scope="col">
          <OwnType
            :text="t('features-column')"
            variant="title-small"
            color="secondary"
          />
        </th>
        <th
          v-for="plan in plans"
          :key="plan"
          class="plan-comparison-table__plan-column"
          scope="col"
        >
          <OwnType :text="plan" variant="title-small" color="secondary" />
        </th>
      </tr>
    </thead>
    <tbody>
      <template v-for="section in sections" :key="section.title">
        <tr>
          <th
            class="plan-comparison-table__section text-left"
            :colspan="plans.length + 1"
            scope="colgroup"
          >
            <OwnType
              :text="section.title"
              variant="title-very-small"
              color="secondary"
            />
          </th>
        </tr>
        <tr
          v-for="row in section.rows"
          :key="row.name"
          :data-testid="`plan-comparison-row-${toTestId(row.name)}`"
        >
          <th class="text-left" scope="row">
            <OwnType :text="row.name" variant="paragraph-small" />
          </th>
          <td
            v-for="(plan, planIndex) in plans"
            :key="plan"
            class="plan-comparison-table__availability"
          >
            <span class="plan-comparison-table__mark">
              <Checkmark1
                v-if="row.availability[planIndex]"
                :size="16"
                :aria-label="t('included', { plan })"
                role="img"
              />
              <span
                v-else
                class="plan-comparison-table__not-included"
                :aria-label="t('not-included', { plan })"
                role="img"
              >
                —
              </span>
            </span>
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<script lang="ts" setup>
import { Checkmark1 } from '@owner/icons-vue/Checkmark1'
import { useI18n } from 'vue-i18n'

import { OwnType } from '@/ui/OwnType'

import type { PlanComparisonSection } from '../types'
import { toTestId } from '../utils'

withDefaults(
  defineProps<{
    caption?: string
    plans: string[]
    sections: PlanComparisonSection[]
  }>(),
  {
    caption: undefined,
  }
)

const { t } = useI18n()
</script>

<style lang="scss" scoped>
.plan-comparison-table {
  max-width: 800px;
  border-collapse: collapse;

  &__caption {
    padding-bottom: 16px;
  }

  th,
  td {
    padding: 8px 16px;
    border-bottom: 1px solid var(--divider);
  }

  &__plan-column,
  &__availability {
    width: 100px;
    text-align: center;
  }

  &__availability {
    color: var(--text-color-secondary);
  }

  // The check icon renders as a block-level svg, so text-align alone leaves it
  // left-aligned; flex-center both marks so checks and dashes line up.
  &__mark {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__not-included {
    color: var(--text-color-tertiary);
  }
}
</style>
